import { useState, useRef } from 'react';

const generateNotificationId = () =>
  (Math.random().toString(36) + Date.now().toString(36)).substr(2, 10);

type StyleType = 'default' | 'success' | 'warning' | 'error';

type Notification = {
  title: string;
  message: string;
  style: StyleType;
  id: string;
  isVisible: boolean;
};

export const useNotifications = (): {
  notificationsList: Notification[];
  addNotification: (title: string, message: string, style?: StyleType) => void;
  hideNotification: (id: string) => void;
} => {
  const [notificationsList, setNotificationsList] = useState<Notification[]>(
    [],
  );

  // This allows notificationsList to be used in setIntervals
  // that would otherwise just use the state at the time of
  // the method being called
  const notificationsListRef = useRef(notificationsList);
  notificationsListRef.current = notificationsList;

  const deleteNotification = (id: string) => {
    const { current: notifications } = notificationsListRef;

    const index = notifications.findIndex(e => e.id === id);
    if (index === -1) {
      return;
    }

    notifications.splice(index, 1);
    setNotificationsList([...notifications]);
  };

  const hideNotification = (id: string) => {
    const { current: notifications } = notificationsListRef;

    const index = notifications.findIndex(e => e.id === id);
    if (index === -1) {
      return;
    }

    notifications[index] = {
      ...notifications[index],
      isVisible: false,
    };

    setNotificationsList([...notifications]);
    setTimeout(() => {
      deleteNotification(notifications[index].id);
    }, 6000);
  };

  const showNotification = (id: string) => {
    const { current: notifications } = notificationsListRef;

    const index = notifications.findIndex(e => e.id === id);
    if (index === -1) {
      return;
    }

    notifications[index] = {
      ...notifications[index],
      isVisible: true,
    };
    setNotificationsList([...notifications]);
    setTimeout(() => {
      hideNotification(id);
    }, 6000);
  };

  const addNotification = (
    title: string,
    message: string,
    style: StyleType = 'default',
  ) => {
    const id = generateNotificationId();
    setNotificationsList(prevArray => [
      ...prevArray,
      {
        title,
        message,
        style,
        id,
        isVisible: false,
      },
    ]);
    setTimeout(() => {
      showNotification(id);
    }, 0);
  };

  return { notificationsList, addNotification, hideNotification };
};
